import type { ElementRef, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DecimalPipe } from '@angular/common';
import { CdkDrag, CdkDragPreview, CdkDragPlaceholder, CdkDropList, CdkDragHandle } from '@angular/cdk/drag-drop';
import { TranslateModule } from '@ngx-translate/core';

import { Widgets } from '@common/enums';

import {
    animationOpacityVar,
    backgroundFilterVar,
    backgroundLayerVar,
    backgroundMarginVar,
    backgroundVar,
    borderRadiusCornerVar,
    borderSideVar,
    columnGapHalfVar,
    columnWidth,
    flexAlignVar,
    responsiveSpacingSideVar,
    sectionColumnGap,
    sectionIsFlexibleWidths,
    shadowVar,
} from '@sites/entity-core';
import type { ColumnDefaultForm } from '@sites/entity-signal-form';

import type { DenormalizedGridEntity, WebLegacyWidgetEntity, DenormalizedTabEntity } from '@web/types';

import { environment } from '@web-env/environment';

import { SingleHoverDirective } from '@web/layout/content/page-content/directives/single-hover.directive';

import { PageStructureService } from '@web/store/page-structure/page-structure/page-structure.service';

import { DefaultColumnControlsComponent } from '@web/layout/content/page-content/components/controls';
import { DragHandleComponent } from '@web/layout/content/page-content/components/drag-handle/drag-handle.component';

import { DefaultGridComponent } from '@web/layout/content/page-content/components/grids';
import { DefaultTabComponent } from '@web/layout/content/page-content/components/tabs';
import { DefaultWidgetComponent } from '@web/layout/content/page-content/components/widgets';

import { AppFacade } from '@web/store/app/app.facade';
import { WebColumnFormService } from '@web/core/services/web-column-form.service';

import type { ColumnType } from '../base-column.abstract';
import { BaseColumnComponent } from '../base-column.abstract';
import { CSS_RESOLVERS } from '@web/core/constants/css-resolvers.const';

type ColumnChild = WebLegacyWidgetEntity | DenormalizedGridEntity | DenormalizedTabEntity;

@Component({
    selector: 'web-column',
    templateUrl: './default-column.component.html',
    styleUrls: ['./default-column.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateModule,
        DecimalPipe,
        CdkDrag,
        CdkDragPreview,
        CdkDragPlaceholder,
        CdkDropList,
        CdkDragHandle,
        SingleHoverDirective,
        DefaultColumnControlsComponent,
        DragHandleComponent,
        DefaultGridComponent,
        DefaultWidgetComponent,
        DefaultTabComponent,
    ],
})
export class DefaultColumnComponent extends BaseColumnComponent implements OnInit {
    public readonly columnType: ColumnType = 'default';

    private readonly appFacade: AppFacade = inject(AppFacade);

    /**
     * Styling is read from the signal form rather than the entity input, the way
     * `default-tab` does it. Each settings group is its own signal, so editing a
     * padding no longer recomputes the background — which with a single bag it
     * did, for all 58 properties at once.
     */
    private form!: ColumnDefaultForm;

    private readonly animatedElem = viewChild.required<ElementRef<HTMLDivElement>>('animatedElem');

    public readonly isEcommerceMode = toSignal(this.appFacade.isEcommerceMode$, { initialValue: false });

    /** Drop targets for the widget drag — every default column on the page. */
    public readonly connectedTo = toSignal(this.blocksFacade.columnConnectedToList$, { initialValue: [] as string[] });

    public readonly widgets = computed<ColumnChild[]>(() => this.entity().children);

    /**
     * The cart is a singleton per site, so the column holding it is pinned: it
     * can neither be copied nor removed.
     */
    public readonly isCartWidgetInside = computed<boolean>(() =>
        this.widgets().some((entity) => this.isEntityWidget(entity) && entity.name === Widgets.SHOP_CART)
    );

    // One signal per settings group, each a single FieldTree node.
    private readonly paddingValue = computed(() => this.form.padding().value());
    private readonly radiusValue = computed(() => this.form.borderRadius().value());
    private readonly borderValue = computed(() => this.form.border().value());
    private readonly backgroundValue = computed(() => this.form.background().value());

    /** Readout for the resize badge; the template only shows it on a flexible row. */
    public readonly widthPercent = computed<number>(() => columnWidth(this.entity()));

    /** Styling of the column box — see `column.base.less`. */
    public readonly gapHalf = computed(() => columnGapHalfVar(sectionColumnGap(this.parentEntity())));
    public readonly justify = computed(() => flexAlignVar(this.form.verticalAlign().value()));
    public readonly shadow = computed(() => shadowVar(this.form.shadow().value()));
    public readonly opacity = computed(() => animationOpacityVar(this.form.animation().value(), false));

    public readonly radiusTl = computed(() => borderRadiusCornerVar(this.radiusValue(), 'topLeft'));
    public readonly radiusTr = computed(() => borderRadiusCornerVar(this.radiusValue(), 'topRight'));
    public readonly radiusBr = computed(() => borderRadiusCornerVar(this.radiusValue(), 'bottomRight'));
    public readonly radiusBl = computed(() => borderRadiusCornerVar(this.radiusValue(), 'bottomLeft'));
    public readonly paddingTop = computed(() => responsiveSpacingSideVar(this.paddingValue(), 'top'));
    public readonly paddingRight = computed(() => responsiveSpacingSideVar(this.paddingValue(), 'right'));
    public readonly paddingBottom = computed(() => responsiveSpacingSideVar(this.paddingValue(), 'bottom'));
    public readonly paddingLeft = computed(() => responsiveSpacingSideVar(this.paddingValue(), 'left'));
    public readonly borderTop = computed(() => borderSideVar(this.borderValue(), 'top'));
    public readonly borderRight = computed(() => borderSideVar(this.borderValue(), 'right'));
    public readonly borderBottom = computed(() => borderSideVar(this.borderValue(), 'bottom'));
    public readonly borderLeft = computed(() => borderSideVar(this.borderValue(), 'left'));
    public readonly bg = computed(() => backgroundVar(this.backgroundValue(), CSS_RESOLVERS));
    public readonly bgPosition = computed(() => backgroundLayerVar(this.backgroundValue(), 'position'));
    public readonly bgRepeat = computed(() => backgroundLayerVar(this.backgroundValue(), 'repeat'));
    public readonly bgSize = computed(() => backgroundLayerVar(this.backgroundValue(), 'size'));
    public readonly bgFilter = computed(() => backgroundFilterVar(this.backgroundValue()));
    public readonly bgMargin = computed(() => backgroundMarginVar(this.backgroundValue()));

    // --- mobile ---
    // Every responsive leaf carries all three breakpoints: v0 resolved the
    // "device inherits desktop" cascade — 15px sides included — at migration.
    public readonly radiusTlMobile = computed(() => borderRadiusCornerVar(this.radiusValue(), 'topLeft', 'mobile'));
    public readonly radiusTrMobile = computed(() => borderRadiusCornerVar(this.radiusValue(), 'topRight', 'mobile'));
    public readonly radiusBrMobile = computed(() => borderRadiusCornerVar(this.radiusValue(), 'bottomRight', 'mobile'));
    public readonly radiusBlMobile = computed(() => borderRadiusCornerVar(this.radiusValue(), 'bottomLeft', 'mobile'));
    public readonly paddingTopMobile = computed(() => responsiveSpacingSideVar(this.paddingValue(), 'top', 'mobile'));
    public readonly paddingRightMobile = computed(() =>
        responsiveSpacingSideVar(this.paddingValue(), 'right', 'mobile')
    );
    public readonly paddingBottomMobile = computed(() =>
        responsiveSpacingSideVar(this.paddingValue(), 'bottom', 'mobile')
    );
    public readonly paddingLeftMobile = computed(() => responsiveSpacingSideVar(this.paddingValue(), 'left', 'mobile'));
    public readonly borderTopMobile = computed(() => borderSideVar(this.borderValue(), 'top', 'mobile'));
    public readonly borderRightMobile = computed(() => borderSideVar(this.borderValue(), 'right', 'mobile'));
    public readonly borderBottomMobile = computed(() => borderSideVar(this.borderValue(), 'bottom', 'mobile'));
    public readonly borderLeftMobile = computed(() => borderSideVar(this.borderValue(), 'left', 'mobile'));
    public readonly bgMobile = computed(() => backgroundVar(this.backgroundValue(), CSS_RESOLVERS, 'mobile'));
    public readonly bgPositionMobile = computed(() => backgroundLayerVar(this.backgroundValue(), 'position', 'mobile'));
    public readonly bgRepeatMobile = computed(() => backgroundLayerVar(this.backgroundValue(), 'repeat', 'mobile'));
    public readonly bgSizeMobile = computed(() => backgroundLayerVar(this.backgroundValue(), 'size', 'mobile'));
    public readonly bgFilterMobile = computed(() => backgroundFilterVar(this.backgroundValue(), 'mobile'));
    public readonly bgMarginMobile = computed(() => backgroundMarginVar(this.backgroundValue(), 'mobile'));

    // --- tablet ---
    public readonly radiusTlTablet = computed(() => borderRadiusCornerVar(this.radiusValue(), 'topLeft', 'tablet'));
    public readonly radiusTrTablet = computed(() => borderRadiusCornerVar(this.radiusValue(), 'topRight', 'tablet'));
    public readonly radiusBrTablet = computed(() => borderRadiusCornerVar(this.radiusValue(), 'bottomRight', 'tablet'));
    public readonly radiusBlTablet = computed(() => borderRadiusCornerVar(this.radiusValue(), 'bottomLeft', 'tablet'));
    public readonly paddingTopTablet = computed(() => responsiveSpacingSideVar(this.paddingValue(), 'top', 'tablet'));
    public readonly paddingRightTablet = computed(() =>
        responsiveSpacingSideVar(this.paddingValue(), 'right', 'tablet')
    );
    public readonly paddingBottomTablet = computed(() =>
        responsiveSpacingSideVar(this.paddingValue(), 'bottom', 'tablet')
    );
    public readonly paddingLeftTablet = computed(() => responsiveSpacingSideVar(this.paddingValue(), 'left', 'tablet'));
    public readonly borderTopTablet = computed(() => borderSideVar(this.borderValue(), 'top', 'tablet'));
    public readonly borderRightTablet = computed(() => borderSideVar(this.borderValue(), 'right', 'tablet'));
    public readonly borderBottomTablet = computed(() => borderSideVar(this.borderValue(), 'bottom', 'tablet'));
    public readonly borderLeftTablet = computed(() => borderSideVar(this.borderValue(), 'left', 'tablet'));
    public readonly bgTablet = computed(() => backgroundVar(this.backgroundValue(), CSS_RESOLVERS, 'tablet'));
    public readonly bgPositionTablet = computed(() => backgroundLayerVar(this.backgroundValue(), 'position', 'tablet'));
    public readonly bgRepeatTablet = computed(() => backgroundLayerVar(this.backgroundValue(), 'repeat', 'tablet'));
    public readonly bgSizeTablet = computed(() => backgroundLayerVar(this.backgroundValue(), 'size', 'tablet'));
    public readonly bgFilterTablet = computed(() => backgroundFilterVar(this.backgroundValue(), 'tablet'));
    public readonly bgMarginTablet = computed(() => backgroundMarginVar(this.backgroundValue(), 'tablet'));

    constructor() {
        super();
        this.replayAnimationOnChange(this.animatedElem);
    }

    public ngOnInit(): void {
        this.form = this.columnFormService.getOrCreateScoped(this.entity().id, this.destroyRef) as ColumnDefaultForm;
    }

    /** The width label only makes sense on a row that stores percentages. */
    public isSectionFlexibleWidths(): boolean {
        return sectionIsFlexibleWidths(this.parentEntity());
    }

    public isEntityGrid(entity: ColumnChild): entity is DenormalizedGridEntity {
        return PageStructureService.isEntityGrid(entity);
    }

    public isEntityTab(entity: ColumnChild): entity is DenormalizedTabEntity {
        return PageStructureService.isEntityTab(entity);
    }

    public isEntityWidget(entity: ColumnChild): entity is WebLegacyWidgetEntity {
        return PageStructureService.isEntityWidget(entity);
    }
}
